import { IApiSuccessWrapper, IExportStatus } from '$/types'
import { apiSlice } from 'app/services/api'

import {
  IQRAChartBaseObject,
  IQRAChartObjects,
  IQRAConsequenceChartGrouping,
  IQRAConsequenceChartGroupingConfig,
  IQRAConsequenceChartObject,
  IQRAGraphBools,
  IQRAGraphExportQueryParams,
  IQRAGraphRequestParamsExport,
  IQRARiskByRoute,
  IQRARiskByRouteChartObject,
  IQRAThreatChartGroupingConfig,
  IQRAThreatChartObject,
  IQRAThreatChartSourceCategory,
  IQRAThreatChartValue,
  QRAGraphType
} from 'features/RiskManagement/Components/Graphs'
import { RiskForecastGraphs } from 'features/RiskManagement/Components/Graphs/Implementations/RiskForecastGraphs'
import {
  convertRiskUnitToAPIRiskValueParameter,
  ILocalFilter,
  RiskUnitType
} from 'features/RiskManagement/Components/LocalFilter'

import { normalizeRiskForecastGraphResponse } from './riskForecastGraph.mapper'
import type {
  IQRARiskForecastGraphApiResult,
  IQRARiskForecastGraphData
} from './riskForecastGraph.mapper'

const SYSTEM_SCOPE_ID_PARAM_NAME = 'systemScopeId'

export interface IGetQRAGraphsQueryParams {
  systemScopeId: string
  localFilter: ILocalFilter
  riskUnit: RiskUnitType
  graphSelections: IQRAGraphBools
  savedStateId1?: string
  savedStateId2?: string
  assessmentId?: string
  continuousRouteId?: string
}

export interface IQRAGraphCsvExportParams {
  systemScopeId: string
  continuousRouteId?: string
  savedStateId?: string
  riskUnit: RiskUnitType
  localFilter: ILocalFilter
}

type QraDedicatedPieGraphRequestParams = Pick<
  IQRAGraphRequestParamsExport,
  'localFilter' | 'riskValue'
>

interface IQraDedicatedPieCsvExportRequestBody {
  assessmentId?: number | null
  systemScopeId: number
  savedStateId?: number | null
  continuousRouteId?: number | null
  graphRequestParams: QraDedicatedPieGraphRequestParams
}

interface IQraPipelineLengthCsvExportRequestBody
  extends IQraDedicatedPieCsvExportRequestBody {
  baselineAcceptanceCategory?: number[]
  wisAcceptanceCategory?: number[]
}

interface IQraRiskBySystemCsvExportRequestBody
  extends IQraDedicatedPieCsvExportRequestBody {
  baselineSystemIds: number[]
  scenarioSystemIds?: number[]
}

interface IQraRiskForecastCsvExportRequestBody
  extends IQraDedicatedPieCsvExportRequestBody {
  forecastChartType: RiskForecastGraphs
  enabledThreatLabels: string[]
}

export type IGetQRAGraphByDimensionQueryParams = Omit<
  IGetQRAGraphsQueryParams,
  'graphSelections'
>

export interface IQRAThreatGraphCategoryResult {
  key: string
  label: string
  sortOrder: number
  values: IQRAThreatChartValue[]
}

export type IQRAThreatGraphGroupingConfigResult = IQRAThreatChartGroupingConfig

export interface IQRAThreatGraphApiResult {
  categoriesResult: IQRAThreatGraphCategoryResult[]
  groupingConfigResult: IQRAThreatGraphGroupingConfigResult[]
}

export interface IQRAThreatGraphData {
  chart: IQRAThreatChartObject
  groupingConfig: IQRAThreatGraphGroupingConfigResult[]
}

export interface IQRAConsequenceGraphCategoryResult {
  key: string
  label: string
  sortOrder: number
  values: IQRAThreatChartValue[]
}

export type IQRAConsequenceGraphGroupingResult = IQRAConsequenceChartGrouping

export type IQRAConsequenceGraphGroupingConfigResult =
  IQRAConsequenceChartGroupingConfig

export interface IQRAConsequenceGraphApiResult {
  categoriesResult: IQRAConsequenceGraphCategoryResult[]
  groupingConfigResult: IQRAConsequenceGraphGroupingConfigResult[]
}

export interface IQRAConsequenceGraphData {
  chart: IQRAConsequenceChartObject
  groupingConfig: IQRAConsequenceGraphGroupingConfigResult[]
}

export interface IQRARiskByRouteGraphApiResult {
  routes: IQRARiskByRoute[]
  groupingConfigResult: IQRAThreatGraphGroupingConfigResult[]
}

export interface IQRARiskByRouteGraphData {
  chart: IQRARiskByRouteChartObject
}

export interface IQRARiskByCategoryGraphValue {
  key: string
  value: number
}

export interface IQRARiskByCategoryGraphCategoryResult {
  id: number
  name: string
  values?: IQRARiskByCategoryGraphValue[]
}

export interface IQRAAcceptanceCategoryResult {
  id: number
  minValue: number
  maxValue: number
  maxDensityValue: number
  minDensityValue: number
  code: string
  name: string
  colour: string
  enabled: boolean
  canBeDeleted: boolean
  assessmentRiskLevels: unknown[]
}

export interface IQRARiskByCategoryGraphApiResult {
  categoriesResult: IQRARiskByCategoryGraphCategoryResult[]
  totalPipeLength: number
  acceptanceCategories: IQRAAcceptanceCategoryResult[]
}

export interface IQRARiskBySystemGraphApiResult {
  categoriesResult: IQRARiskByCategoryGraphCategoryResult[]
  totalRiskBaseline: number
  totalRiskScenario?: number
}

const buildQraGraphRequest = ({
  systemScopeId,
  localFilter,
  riskUnit,
  savedStateId1,
  savedStateId2,
  assessmentId,
  continuousRouteId
}: IGetQRAGraphByDimensionQueryParams) => ({
  method: 'POST' as const,
  params: {
    [SYSTEM_SCOPE_ID_PARAM_NAME]: systemScopeId,
    savedStateId: savedStateId1,
    savedStateId1,
    savedStateId2,
    assessmentId,
    continuousRouteId
  },
  body: {
    localFilter,
    riskValue: convertRiskUnitToAPIRiskValueParameter(riskUnit)
  }
})

export const normalizeRiskByThreatGraphResponse = (
  result?: IQRAThreatGraphApiResult | null
): IQRAThreatGraphData => {
  const categories: IQRAThreatChartSourceCategory[] = [
    ...(result?.categoriesResult ?? [])
  ].sort((left, right) => left.sortOrder - right.sortOrder)

  const groupingConfig: IQRAThreatChartGroupingConfig[] = [
    ...(result?.groupingConfigResult ?? [])
  ]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((groupingConfigItem) => ({
      ...groupingConfigItem,
      groupings: [...groupingConfigItem.groupings].sort(
        (left, right) => left.sortOrder - right.sortOrder
      )
    }))

  return {
    chart: {
      graphType: QRAGraphType.THREATS,
      groupingConfig,
      sourceCategories: categories,
      items: categories.map((category, index) => ({
        id: category.sortOrder ?? index + 1,
        name: category.label,
        values: category.values
      }))
    },
    groupingConfig
  }
}

export const normalizeRiskByConsequenceGraphResponse = (
  result?: IQRAConsequenceGraphApiResult | null
): IQRAConsequenceGraphData => {
  const categories: IQRAConsequenceGraphCategoryResult[] = [
    ...(result?.categoriesResult ?? [])
  ].sort((left, right) => left.sortOrder - right.sortOrder)

  const groupingConfig: IQRAConsequenceChartGroupingConfig[] = [
    ...(result?.groupingConfigResult ?? [])
  ]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((groupingConfigItem) => ({
      ...groupingConfigItem,
      groupings: [...groupingConfigItem.groupings].sort(
        (left, right) => left.sortOrder - right.sortOrder
      )
    }))

  return {
    chart: {
      graphType: QRAGraphType.CONSEQUENCES,
      groupingConfig,
      sourceCategories: categories,
      items: categories.map((category, index) => ({
        id: category.sortOrder ?? index + 1,
        name: category.label,
        values: category.values
      }))
    },
    groupingConfig
  }
}

export const normalizeRiskByRouteGraphResponse = (
  result?: IQRARiskByRouteGraphApiResult | null
): IQRARiskByRouteGraphData => ({
  chart: {
    graphType: QRAGraphType.RISK_BY_LINE,
    routes: (result?.routes ?? []).map((route) => ({
      ...route,
      categoriesResult: [...route.categoriesResult].sort(
        (left, right) => left.sortOrder - right.sortOrder
      )
    })),
    groupingConfig: [...(result?.groupingConfigResult ?? [])]
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map((config) => ({
        ...config,
        groupings: [...config.groupings].sort(
          (left, right) => left.sortOrder - right.sortOrder
        )
      }))
  }
})

export const qraGraphsApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getQRAGraphs: build.query<IQRAChartObjects, IGetQRAGraphsQueryParams>({
      query: ({
        systemScopeId,
        localFilter,
        riskUnit,
        graphSelections,
        savedStateId1,
        savedStateId2,
        assessmentId,
        continuousRouteId
      }) => {
        const request = buildQraGraphRequest({
          systemScopeId,
          localFilter,
          riskUnit,
          savedStateId1,
          savedStateId2,
          assessmentId,
          continuousRouteId
        })

        return {
          ...request,
          url: '/qra/graphs',
          body: {
            ...request.body,
            graphSelection: graphSelections
          }
        }
      },
      // providesTags: (_res, _err, args) => [{ type: 'Graphs', systemScopeId: args.systemScopeId }],
      transformResponse: (res: IApiSuccessWrapper) => {
        const charts: IQRAChartObjects = {}

        if (res?.result != null) {
          const rawCharts = res.result as IQRAChartBaseObject[]

          /**
           * Transform result from:
           *
           * result: [
           *   { graphType: 'riskbythreat, items: [] },
           *   { graphType: 'riskbyconsequence', items: [] },
           *   ...
           * ]
           *
           * To:
           *
           * charts: {
           *   riskbythreat: { graphType: 'riskbythreat, items: [] },
           *   riskbyconsequence: { graphType: 'riskbyconsequence', items: [] },
           *   ...
           * }
           */
          for (const chart of rawCharts) {
            charts[chart.graphType] = chart
          }
        }

        return charts
      }
    }),
    getQRARiskByThreatGraph: build.query<
      IQRAThreatGraphData,
      IGetQRAGraphByDimensionQueryParams
    >({
      query: (args) => ({
        ...buildQraGraphRequest(args),
        url: '/qra/graphs/riskbythreatgraph'
      }),
      transformResponse: (res: IApiSuccessWrapper) => {
        return normalizeRiskByThreatGraphResponse(
          (res?.result as IQRAThreatGraphApiResult | undefined) ?? null
        )
      }
    }),
    getQRARiskByConsequenceGraph: build.query<
      IQRAConsequenceGraphData,
      IGetQRAGraphByDimensionQueryParams
    >({
      query: (args) => ({
        ...buildQraGraphRequest(args),
        url: '/qra/graphs/riskbyconsequencegraph'
      }),
      transformResponse: (res: IApiSuccessWrapper) => {
        return normalizeRiskByConsequenceGraphResponse(
          (res?.result as IQRAConsequenceGraphApiResult | undefined) ?? null
        )
      }
    }),
    getQRARiskByRouteGraph: build.query<
      IQRARiskByRouteGraphData,
      IGetQRAGraphByDimensionQueryParams
    >({
      query: (args) => ({
        ...buildQraGraphRequest(args),
        url: '/qra/graphs/riskbyroutegraph'
      }),
      transformResponse: (res: IApiSuccessWrapper) =>
        normalizeRiskByRouteGraphResponse(
          (res?.result as IQRARiskByRouteGraphApiResult | undefined) ?? null
        )
    }),
    getQRARiskForecastGraph: build.query<
      IQRARiskForecastGraphData,
      IGetQRAGraphByDimensionQueryParams
    >({
      query: (args) => ({
        ...buildQraGraphRequest(args),
        url: '/qra/graphs/riskforecastgraph'
      }),
      transformResponse: (res: IApiSuccessWrapper) => {
        return normalizeRiskForecastGraphResponse(
          (res?.result as IQRARiskForecastGraphApiResult | undefined) ?? null
        )
      }
    }),
    getQRARiskByPipelineLengthGraph: build.query<
      IQRARiskByCategoryGraphApiResult,
      IGetQRAGraphByDimensionQueryParams
    >({
      query: (args) => ({
        ...buildQraGraphRequest(args),
        url: '/qra/graphs/riskbypipelinelengthgraph'
      }),
      transformResponse: (
        res: IApiSuccessWrapper<IQRARiskByCategoryGraphApiResult>
      ) => {
        return res.result
      }
    }),
    getQRARiskBySystemGraph: build.query<
      IQRARiskBySystemGraphApiResult,
      IGetQRAGraphByDimensionQueryParams
    >({
      query: (args) => ({
        ...buildQraGraphRequest(args),
        url: '/qra/graphs/riskbysystemgraph'
      }),
      transformResponse: (
        res: IApiSuccessWrapper<IQRARiskBySystemGraphApiResult>
      ) => {
        return res.result
      }
    }),
    exportQRARiskByThreatGraph: build.mutation<
      IExportStatus,
      IQRAGraphExportQueryParams
    >({
      query: ({
        systemScopeId,
        continuousRouteId,
        assessmentId,
        threatConfigurationKey,
        sortOptions,
        groupingOptions,
        graphRequestParams,
        savedStateId
      }) => {
        const request = buildQraGraphCsvExportRequest({
          systemScopeId,
          continuousRouteId,
          assessmentId,
          threatConfigurationKey,
          sortOptions,
          groupingOptions,
          graphRequestParams,
          savedStateId
        })

        return {
          ...request,
          url: '/qra/graphs/exportRiskByThreat',
          body: {
            ...request.body
          }
        }
      },
      transformResponse: (res: IApiSuccessWrapper) => {
        return res.result as IExportStatus
      }
    }),
    exportQRAConsequenceGraph: build.mutation<
      IExportStatus,
      IQRAGraphExportQueryParams
    >({
      query: ({
        systemScopeId,
        continuousRouteId,
        assessmentId,
        threatConfigurationKey,
        sortOptions,
        groupingOptions,
        graphRequestParams,
        savedStateId
      }) => {
        const request = buildQraGraphCsvExportRequest({
          systemScopeId,
          continuousRouteId,
          assessmentId,
          threatConfigurationKey,
          sortOptions,
          groupingOptions,
          graphRequestParams,
          savedStateId
        })

        return {
          ...request,
          url: '/qra/graphs/exportRiskByConsequence',
          body: {
            ...request.body
          }
        }
      },
      transformResponse: (res: IApiSuccessWrapper) => {
        return res.result as IExportStatus
      }
    }),
    exportQRARiskByPipelineLengthGraph: build.mutation<
      IExportStatus,
      IQRAGraphExportQueryParams
    >({
      query: ({
        systemScopeId,
        assessmentId,
        graphRequestParams,
        savedStateId,
        continuousRouteId,
        baselineAcceptanceCategory,
        wisAcceptanceCategory
      }) => {
        const request = buildQraPipelineLengthCsvExportRequest({
          systemScopeId,
          assessmentId,
          graphRequestParams,
          savedStateId,
          continuousRouteId,
          baselineAcceptanceCategory,
          wisAcceptanceCategory
        })

        return {
          ...request,
          url: '/qra/graphs/exportRiskByPipelineLength',
          body: {
            ...request.body
          }
        }
      },
      transformResponse: (res: IApiSuccessWrapper) => {
        return res.result as IExportStatus
      }
    }),
    exportQRARiskBySystemGraph: build.mutation<
      IExportStatus,
      IQRAGraphExportQueryParams
    >({
      query: ({
        systemScopeId,
        continuousRouteId,
        assessmentId,
        graphRequestParams,
        savedStateId,
        baselineSystemIds,
        scenarioSystemIds
      }) => {
        const request = buildQraRiskBySystemCsvExportRequest({
          systemScopeId,
          assessmentId,
          graphRequestParams,
          savedStateId,
          continuousRouteId,
          baselineSystemIds,
          scenarioSystemIds
        })

        return {
          ...request,
          url: '/qra/graphs/exportRiskBySystem',
          body: {
            ...request.body
          }
        }
      },
      transformResponse: (res: IApiSuccessWrapper) => {
        return res.result as IExportStatus
      }
    }),
    exportQRARiskForecastGraph: build.mutation<
      IExportStatus,
      IQRAGraphExportQueryParams & {
        forecastChartType: RiskForecastGraphs
        enabledThreatLabels: string[]
      }
    >({
      query: ({
        systemScopeId,
        continuousRouteId,
        assessmentId,
        graphRequestParams,
        savedStateId,
        forecastChartType,
        enabledThreatLabels
      }) => {
        const request = buildQraRiskForecastCsvExportRequest({
          systemScopeId,
          assessmentId,
          graphRequestParams,
          savedStateId,
          continuousRouteId,
          forecastChartType,
          enabledThreatLabels
        })

        return {
          ...request,
          url: '/qra/graphs/exportRiskForecast',
          body: {
            ...request.body
          }
        }
      },
      transformResponse: (res: IApiSuccessWrapper) => {
        return res.result as IExportStatus
      }
    }),
    exportQRARiskByRouteGraph: build.mutation<
      IExportStatus,
      IQRAGraphExportQueryParams & {
        enabledThreatLabels: string[]
      }
    >({
      query: ({
        systemScopeId,
        continuousRouteId,
        assessmentId,
        graphRequestParams,
        savedStateId,
        enabledThreatLabels,
        sortOptions
      }) => {
        const request = buildQraRiskByRouteCsvExportRequest({
          systemScopeId,
          assessmentId,
          graphRequestParams,
          savedStateId,
          continuousRouteId,
          enabledThreatLabels,
          sortOptions
        })

        return {
          ...request,
          url: '/qra/graphs/exportRiskByRoute',
          body: {
            ...request.body
          }
        }
      },
      transformResponse: (res: IApiSuccessWrapper) => {
        return res.result as IExportStatus
      }
    })
  })
})

export const {
  useGetQRAGraphsQuery,
  useGetQRARiskByThreatGraphQuery,
  useExportQRARiskByThreatGraphMutation,
  useGetQRARiskByConsequenceGraphQuery,
  useGetQRARiskByRouteGraphQuery,
  useGetQRARiskForecastGraphQuery,
  useGetQRARiskByPipelineLengthGraphQuery,
  useGetQRARiskBySystemGraphQuery,
  useExportQRAConsequenceGraphMutation,
  useExportQRARiskByPipelineLengthGraphMutation,
  useExportQRARiskBySystemGraphMutation,
  useExportQRARiskForecastGraphMutation,
  useExportQRARiskByRouteGraphMutation
} = qraGraphsApi

const buildQraGraphCsvExportRequest = ({
  systemScopeId,
  continuousRouteId,
  assessmentId,
  threatConfigurationKey,
  sortOptions,
  groupingOptions,
  graphRequestParams,
  savedStateId
}: IQRAGraphExportQueryParams) => ({
  method: 'POST' as const,
  body: {
    systemScopeId,
    continuousRouteId,
    assessmentId,
    threatConfigurationKey,
    sortOptions,
    groupingOptions,
    savedStateId,
    graphRequestParams
  }
})

const buildQraPipelineLengthCsvExportRequest = ({
  systemScopeId,
  assessmentId,
  graphRequestParams,
  savedStateId,
  continuousRouteId,
  baselineAcceptanceCategory,
  wisAcceptanceCategory
}: Pick<
  IQRAGraphExportQueryParams,
  | 'assessmentId'
  | 'baselineAcceptanceCategory'
  | 'graphRequestParams'
  | 'savedStateId'
  | 'systemScopeId'
  | 'continuousRouteId'
  | 'wisAcceptanceCategory'
>) => ({
  method: 'POST' as const,
  body: {
    assessmentId,
    systemScopeId,
    savedStateId,
    continuousRouteId,
    graphRequestParams: getDedicatedPieGraphRequestParams(graphRequestParams),
    ...getPipelineLengthAcceptanceCategories({
      baselineAcceptanceCategory,
      wisAcceptanceCategory
    })
  } satisfies IQraPipelineLengthCsvExportRequestBody
})

const buildQraRiskBySystemCsvExportRequest = ({
  systemScopeId,
  assessmentId,
  graphRequestParams,
  savedStateId,
  continuousRouteId,
  baselineSystemIds,
  scenarioSystemIds
}: Pick<
  IQRAGraphExportQueryParams,
  | 'assessmentId'
  | 'baselineSystemIds'
  | 'graphRequestParams'
  | 'scenarioSystemIds'
  | 'savedStateId'
  | 'systemScopeId'
  | 'continuousRouteId'
>) => ({
  method: 'POST' as const,
  body: {
    assessmentId,
    systemScopeId,
    savedStateId,
    continuousRouteId,
    graphRequestParams: getDedicatedPieGraphRequestParams(graphRequestParams),
    baselineSystemIds: baselineSystemIds ?? [],
    ...(scenarioSystemIds == null ? {} : { scenarioSystemIds })
  } satisfies IQraRiskBySystemCsvExportRequestBody
})

const getDedicatedPieGraphRequestParams = ({
  localFilter,
  riskValue
}: IQRAGraphRequestParamsExport): QraDedicatedPieGraphRequestParams => ({
  localFilter,
  riskValue
})

const getPipelineLengthAcceptanceCategories = ({
  baselineAcceptanceCategory,
  wisAcceptanceCategory
}: Pick<
  IQRAGraphExportQueryParams,
  'baselineAcceptanceCategory' | 'wisAcceptanceCategory'
>) => ({
  ...(baselineAcceptanceCategory == null ? {} : { baselineAcceptanceCategory }),
  ...(wisAcceptanceCategory == null ? {} : { wisAcceptanceCategory })
})

const buildQraRiskForecastCsvExportRequest = ({
  systemScopeId,
  assessmentId,
  graphRequestParams,
  savedStateId,
  continuousRouteId,
  forecastChartType,
  enabledThreatLabels
}: Pick<
  IQRAGraphExportQueryParams,
  | 'assessmentId'
  | 'graphRequestParams'
  | 'savedStateId'
  | 'systemScopeId'
  | 'continuousRouteId'
  | 'forecastChartType'
  | 'enabledThreatLabels'
> & {
  forecastChartType: NonNullable<
    IQRAGraphExportQueryParams['forecastChartType']
  >
  enabledThreatLabels: NonNullable<
    IQRAGraphExportQueryParams['enabledThreatLabels']
  >
}) => ({
  method: 'POST' as const,
  body: {
    assessmentId,
    systemScopeId,
    savedStateId,
    continuousRouteId,
    graphRequestParams: getDedicatedPieGraphRequestParams(graphRequestParams),
    forecastChartType,
    enabledThreatLabels
  } satisfies IQraRiskForecastCsvExportRequestBody
})

const buildQraRiskByRouteCsvExportRequest = ({
  systemScopeId,
  assessmentId,
  graphRequestParams,
  savedStateId,
  continuousRouteId,
  enabledThreatLabels,
  sortOptions
}: Pick<
  IQRAGraphExportQueryParams,
  | 'assessmentId'
  | 'graphRequestParams'
  | 'savedStateId'
  | 'systemScopeId'
  | 'continuousRouteId'
  | 'enabledThreatLabels'
  | 'sortOptions'
> & {
  enabledThreatLabels: NonNullable<
    IQRAGraphExportQueryParams['enabledThreatLabels']
  >
}) => ({
  method: 'POST' as const,
  body: {
    assessmentId,
    systemScopeId,
    savedStateId,
    continuousRouteId,
    graphRequestParams: getDedicatedPieGraphRequestParams(graphRequestParams),
    sortOptions,
    enabledThreatLabels
  }
})
